import { Link } from 'react-router-dom';
import { BUSINESS } from '../../mocks/sharedData';

const NAV_COLS = [
  {
    heading: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Services', href: '/services' },
      { label: 'Service Areas', href: '/locations' },
    ],
  },
  {
    heading: 'Services',
    links: [
      { label: 'Residential Solar', href: '/services' },
      { label: 'Commercial Solar', href: '/services' },
      { label: 'Nonprofit Solar', href: '/services' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Free Estimate', href: BUSINESS.consultationUrl, external: true },
      { label: 'Why Solar?', href: '/services' },
      { label: 'FAQ', href: '/#faq' },
    ],
  },
];

const Footer = () => (
  <footer className="bg-zinc-950 text-zinc-300">
    {/* Top CTA strip */}
    <div className="bg-amber-500">
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-serif text-xl text-zinc-950 font-semibold">
          Ready to start saving with solar?
        </p>
        <a
          href={BUSINESS.consultationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer transition-colors"
        >
          Schedule a Free Consultation
        </a>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
        {/* Brand */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 flex items-center justify-center bg-zinc-800 rounded-full overflow-hidden">
              <img
                src={BUSINESS.logo}
                alt={BUSINESS.name}
                className="w-10 h-10 object-contain"
              />
            </div>
            <div>
              <span className="font-serif font-semibold text-white text-lg leading-none block">
                Star Solar Specialists
              </span>
              <span className="text-amber-500 text-xs tracking-widest uppercase">
                Est. {BUSINESS.founded}
              </span>
            </div>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">
            Family-owned solar installation experts serving Southern Indiana and North Central Kentucky.
            Licensed, insured, and dedicated to bringing affordable solar power to Kentuckiana since 2015.
          </p>
          {/* Social */}
          <div className="flex items-center gap-3">
            {[
              { href: BUSINESS.social.facebook, icon: 'ri-facebook-fill', label: 'Facebook' },
              { href: BUSINESS.social.instagram, icon: 'ri-instagram-line', label: 'Instagram' },
              { href: BUSINESS.social.youtube, icon: 'ri-youtube-fill', label: 'YouTube' },
              { href: BUSINESS.social.twitter, icon: 'ri-twitter-x-fill', label: 'X' },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-zinc-800 hover:bg-amber-500 text-zinc-300 hover:text-white transition-all duration-200 cursor-pointer"
              >
                <i className={`${s.icon} text-base`} />
              </a>
            ))}
          </div>
        </div>

        {/* Nav Columns */}
        {NAV_COLS.map((col) => (
          <div key={col.heading}>
            <h4 className="text-white font-semibold text-sm tracking-widest uppercase mb-5">
              <a href={col.links[0].href}>{col.heading}</a>
            </h4>
            <ul className="space-y-3">
              {col.links.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-amber-400 text-sm transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-zinc-400 hover:text-amber-400 text-sm transition-colors cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Contact */}
        <div>
          <h4 className="text-white font-semibold text-sm tracking-widest uppercase mb-5">
            <a href="/locations">Contact</a>
          </h4>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-map-pin-line text-amber-500" />
              </div>
              <span>{BUSINESS.fullAddress}</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-amber-500" />
              </div>
              <a href={BUSINESS.phoneTel} className="hover:text-amber-400 transition-colors cursor-pointer">
                {BUSINESS.phone}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-amber-500" />
              </div>
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-amber-400 transition-colors cursor-pointer">
                {BUSINESS.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-time-line text-amber-500" />
              </div>
              <span>Mon–Fri &nbsp;9:00 AM – 5:00 PM</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
        <p>© 2015–2024 Star Solar Specialists, LLC. All Rights Reserved.</p>
        <div className="flex items-center gap-4">
          <a href="https://www.starsolarspecialists.com/terms" rel="nofollow" className="hover:text-zinc-300 cursor-pointer">Terms</a>
          <a href="https://www.starsolarspecialists.com/privacy" rel="nofollow" className="hover:text-zinc-300 cursor-pointer">Privacy</a>
          <span>Licensed in Indiana &amp; Kentucky</span>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
